import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CampaignIcon from '@mui/icons-material/Campaign';
import { Link as RouterLink } from 'react-router-dom';

const POST_TYPE_TAGS = {
  announcement: 'Announcement',
  'fitness tip': 'Fitness Tip',
  'nutrition tip': 'Nutrition Tip',
  challenge: 'Challenge',
  motivation: 'Motivation',
  event: 'Event',
};

const postType = (post) =>
  (post.tags || [])
    .map((tag) => POST_TYPE_TAGS[tag.toLowerCase()])
    .find(Boolean) || null;

const typeStyles = (type) => {
  if (type === 'Nutrition Tip') return { color: '#ea580c', bg: '#fff0df', icon: <LocalFireDepartmentIcon /> };
  if (type === 'Motivation') return { color: '#db2777', bg: '#fce7f3', icon: <FavoriteIcon /> };
  if (type === 'Challenge') return { color: '#db2777', bg: '#fce7f3', icon: <FavoriteIcon /> };
  if (type === 'Fitness Tip') return { color: '#16a34a', bg: '#dcfce7', icon: <RestaurantIcon /> };
  return { color: '#4338ca', bg: '#ebe9ff', icon: <CampaignIcon /> };
};

const statusLabel = (post) => {
  if (post.status === 'DRAFT') return 'Draft';
  if (post.status === 'PRO') return 'Pro';
  if (post.status === 'PREMIUM') return 'Premium';
  return 'Published';
};

const statusStyles = (status) => {
  if (status === 'Draft') return { color: '#475569', bg: '#eef2f7' };
  if (status === 'Premium' || status === 'Pro') return { color: '#4338ca', bg: '#ebe9ff' };
  return { color: '#118847', bg: '#dcfce7' };
};

const statsFor = (post) => ({
  comments: post.replies?.length ?? 0,
});

const NoPostImage = () => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: '#f1f5f9',
      borderRadius: 1,
      color: '#64748b',
      display: 'flex',
      fontSize: 13,
      fontWeight: 700,
      height: 96,
      justifyContent: 'center',
      width: 128,
    }}
  >
    No cover
  </Box>
);

const PostList = ({ posts, onDelete, currentUserName }) => {
  return (
    <Stack>
      {(posts || []).map((post, index) => {
        const url = `/trainer/posts/${post._id}`;
        const image = post.coverImageUrl;
        const type = postType(post);
        const typeStyle = type ? typeStyles(type) : null;
        const status = statusLabel(post);
        const statusStyle = statusStyles(status);
        const stats = statsFor(post, index);

        return (
          <Box
            key={post._id}
            sx={{
              borderBottom: '1px solid #e2e8f0',
              px: 1,
              py: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {image ? (
                <Box
                  component="img"
                  src={image}
                  alt={post.title}
                  sx={{ width: 128, height: 96, borderRadius: 1, objectFit: 'cover' }}
                />
              ) : (
                <NoPostImage />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography
                    component={RouterLink}
                    to={url}
                    sx={{
                      color: '#0f172a',
                      fontWeight: 800,
                      fontSize: 18,
                      textDecoration: 'none',
                    }}
                  >
                    {post.title}
                  </Typography>
                  {type && (
                    <Chip
                      size="small"
                      icon={typeStyle.icon}
                      label={type}
                      sx={{
                        color: typeStyle.color,
                        backgroundColor: typeStyle.bg,
                        fontWeight: 800,
                        '& .MuiChip-icon': { color: typeStyle.color },
                      }}
                    />
                  )}
                </Stack>
                <Typography noWrap sx={{ color: '#40536b', mb: 1.25 }}>
                  {(post.content || post.desc || 'A quick update for your community.')
                    .replace(/<[^>]*>/g, '')
                    .slice(0, 110)}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={post.user?.avatarUrl} sx={{ width: 28, height: 28 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                        {post.user?.name || currentUserName || 'Unknown author'}
                      </Typography>
                      <Typography sx={{ color: '#40536b', fontSize: 12 }}>
                        {new Date(post.createdAt).toLocaleString()}
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Box>
              <Chip
                size="small"
                label={status}
                sx={{
                  color: statusStyle.color,
                  backgroundColor: statusStyle.bg,
                  fontWeight: 800,
                  alignSelf: 'flex-start',
                  mt: 1,
                }}
              />
              <Stack direction="row" spacing={4} sx={{ width: 170 }}>
                {[
                  [stats.comments, 'Comments'],
                ].map(([value, label]) => (
                  <Box key={label}>
                    <Typography sx={{ fontWeight: 800 }}>{value}</Typography>
                    <Typography sx={{ color: '#40536b', fontSize: 12 }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Stack spacing={1.25} sx={{ width: 120 }}>
                <Button
                  component={RouterLink}
                  to={url}
                  variant="outlined"
                  startIcon={<EditOutlinedIcon />}
                >
                  Edit
                </Button>
                {post.status === 'DRAFT' ? (
                  <Button variant="contained">Publish</Button>
                ) : (
                  <Button variant="outlined" startIcon={<VisibilityOutlinedIcon />}>
                    Preview
                  </Button>
                )}
              </Stack>
              <IconButton
                onClick={() => onDelete?.(post)}
                sx={{ border: '1px solid #d0d9e6', borderRadius: 1 }}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export { postType, statsFor };
export default PostList;
